import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Header, Message, Table, Button, Icon } from 'react-frontier';
import { Modal } from 'semantic-ui-react';
import moment from 'moment';
import { SetLoading } from '@cocoan/components/Classes';
import { OrderData, OrderFile } from '../ClassesPublic';
import API from '../APIPublic';
import { addCommas, bindClick } from '../UtilPublic';
import { useUser } from '../HooksPublic';
import { CDN_URL, PLACEHOLDER_IMG } from '../ConfigPublic';

const getOrderType = (orderMethod: number): string => {
   switch(orderMethod) {
      case 1: return 'PDV';
      case 2: return 'Online';
      case 20: return 'B2B';
      case 21: return 'Personalizada';
      case 22: return 'Call Center';
      default: return 'Otro';
   }
};

var OrderView = ()=>{
	var { order_hash } = useParams<{ order_hash: string }>();
	var navigate = useNavigate();
	var location = useLocation();
	var { token, login } = useUser() || {};
	var [orderData, setOrderData] = useState<OrderData>(null);
	var [error, setError] = useState<string>(null);
	var [pdfToken, setPdfToken] = useState<string>(null);
	var [viewingFile, setViewingFile] = useState<OrderFile>(null);

	useEffect(()=>{
		var search_params = new URLSearchParams(location.search);
		var queryToken = search_params.get('q');
		if(queryToken) setPdfToken(queryToken);
	}, [location.search]);

	useEffect(()=>{
		if(!order_hash) return navigate('/');
		if(typeof login === 'undefined') return;

		var session_token = token?.access || pdfToken || null;

		setError(null);
		API.getOrderByHash(order_hash, session_token).then(res=>{
			if(res.error)return setError(res.message);
			setOrderData(res.data);
		}).catch(()=>{
			setError('CCN-ORDHASH-1');
		});
	}, [order_hash, navigate, login, token?.access, pdfToken]);

	if(error){
		var isAuthError = error?.includes('ADMNOACC') || error?.includes('ATH-NOACC');
		return(
			<div className="co order-view">
				{isAuthError ? (
					<div style={{ maxWidth: 420, margin: '40px auto', textAlign: 'center' }}>
						<Header text="Acceso restringido" subtext="Esta orden está vinculada a una cuenta. Inicia sesión para verla." size="small" />
						<Button text="Iniciar sesión" color="black" style={{ marginTop: 16 }} onClick={() => { window.location.href = API.getAuthUrl(); }} />
					</div>
				) : (
					<Message type="error" text={error} />
				)}
			</div>
		);
	}

	if (!orderData) {
		return (
			<div className="co order-view">
				<Header text={`Cargando orden...`} loading />
			</div>
		);
	}

	var order = orderData;
	var items = orderData.items;

	var amount_paid = order.payments?.filter(p => p.paid).reduce((sum, p) => sum + p.amount, 0) || 0;
	var has_payment_shortage = amount_paid > 0 && amount_paid < order.total;

	var downloadQuotation = async (setLoading: SetLoading) => {
		if (!order_hash || !pdfToken) {
			setError('No se puede descargar el PDF sin un token válido');
			return;
		}

		setLoading(true);
		try {
			await API.downloadQuotationPdf(order_hash, pdfToken);
		} catch (error) {
			setError('Error al generar el PDF. Por favor, inténtalo de nuevo.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="co order-view">
			<div style={{ maxWidth: 600, margin: '20px auto' }}>
				<Header
					text={`${!!order.quotation ? 'Cotización' : 'Orden'} ${order.order_hash}`}
					size="large"
				/>
				{!!order.quotation && (
					<div style={{ marginBottom: 20, textAlign: 'center' }}>
						<Button
							color="black"
							text="Descargar cotización"
							onClick={downloadQuotation}
						/>
					</div>
				)}
				{has_payment_shortage && (
					<div style={{ marginBottom: 20, textAlign: 'center' }}>
						<Button
							color="black"
							text={`Pagar saldo restante ($${addCommas(order.total - amount_paid)})`}
							onClick={() => navigate(`/checkout/${order.order_hash}`)}
						/>
					</div>
				)}
				<Table
					title='DETALLES'
					fitted
					details
					striped
					style={{ padding: 0, marginBottom: 20, paddingBottom: 0 }}
				>
					<Table.Row data={[`No. de ${!!order.quotation ? 'cotización' : 'orden'}`, order.order_hash]} />
					{order.order_method && (
						<Table.Row data={[
							'Método',
							getOrderType(order.order_method)
						]} />
					)}
					<Table.Row data={['Entrega', order.shipping_method_name || 'No especificado']} />
					{!order.quotation && (
						<Table.Row data={['Estatus', <>
							<div className={`fr tiny label ${!order.paid ? 'red' : 'green'}`}>
								{order.paid ? 'Pagado' : 'No pagado'}
							</div>
							{!!order.shipped && (
								<div className="fr tiny label blue" style={{ marginLeft: 5 }}>
									Enviado
								</div>
							)}
							{has_payment_shortage && (
								<div className='fr orange tiny label' style={{ marginLeft: 5 }}>
									Pago faltante
								</div>
							)}
						</>]}
						/>
					)}
					{!!order.quotation && (
						<Table.Row data={['Estatus',
							<div className="fr tiny label blue">
								Cotización
							</div>
						]} />
					)}
					<Table.Row data={['Creación', moment.unix(order.date_created).format('DD/MMM/YY HH:mm')]} />
					{!!order.date_paid && order.date_paid > 0 && (
						<Table.Row data={['Fecha de pago', moment.unix(order.date_paid).format('DD/MMM/YY HH:mm')]} />
					)}
					{order.order_method !== 1 && (
						<>
							<Table.Divider />
							<Table.Row data={['Cliente', `${order.owner_name} ${order.owner_last_name || ''}`.trim()]} />
							<Table.Row data={['Email', order.owner_email]} />
							<Table.Row data={['Teléfono', order.owner_phone || 'No especificado']} />
							<Table.Divider />
						</>
					)}
					{order.order_method === 1 && <Table.Divider />}
					{!!order.shipped && (
						<Table.Row data={['Entrega', 'Envío a domicilio']} />
					)}
					{!!(order.shipping_date && order.shipping_date > 0) && (
						<Table.Row data={['Fecha solicitada envío', moment.unix(order.shipping_date).format('DD/MMM/YY')]} />
					)}
					{!!(order.shipped && !!order.shipping_date && order.shipping_date > 0) && (
						<Table.Row data={['Fecha enviada', moment.unix(order.shipping_date).format('DD/MMM/YY HH:mm')]} />
					)}
					<Table.Divider />
					<Table.Row data={['Total', `$${addCommas(order.total)}`]} />
				</Table>
				<Table
					fitted
					striped
					title='Artículos'
					headers={['Producto', 'Cantidad', 'Precio', 'Subtotal']}
					style={{ marginBottom: 20 }}
					collapsingIndexes={[1, 2, 3]}
					centeredIndexes={[1, 2, 3]}
				>
					{items.map(item=>(
						<Table.Row 
							key={item.product_id}
							data={[
								<div key="product" style={{ display: 'flex', alignItems: 'center' }}>
									<img
										src={`${CDN_URL}/products/${item.product_id}/0.webp`}
										onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER_IMG; }}
										style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, marginRight: 12, backgroundColor: '#f5f5f5', flexShrink: 0 }}
										alt={item.product_name}
									/>
									<div>
										<div style={{ fontWeight: 'bold' }}>{item.product_name}</div>
										{!!item.size && <small style={{ color: '#666' }}>({item.size})</small>}
									</div>
								</div>,
								addCommas(item.quantity),
								`$${addCommas(item.cost_single)}`,
								`$${addCommas(item.cost)}`
							]}
						/>
					))}
				</Table>
				{!order.quotation && !!order.payments && order.payments.length > 0 && (
					<Table
						striped
						title='Pagos'
						headers={['ID', 'Método', 'Estatus', 'Fecha', 'Monto']}
						style={{ marginBottom: 20 }}
						collapsingIndexes={[0, 2]}
						centeredIndexes={[2, 4]}
						data={order.payments.map(payment => [
							payment.payment_id,
							payment.method_name || 'N/A',
							<span className={`fr tiny label ${payment.paid ? 'green' : 'red'}`}>
								{payment.paid ? 'Pagado' : 'No pagado'}
							</span>,
							payment.date_paid ? moment.unix(payment.date_paid).format('DD/MM/YY HH:mm') : <Icon color='gray' name='minus' />,
							`$${addCommas(payment.amount)}`
						])}
					/>
				)}
				{!!order.gift_message && (
					<Message type="info" text={order.gift_message} />
				)}
				{!!(order.files && order.files.length > 0) && (
					<Table
						striped
						title='Propuestas'
						headers={['Nombre', 'Descripción', '']}
						style={{ marginBottom: 20 }}
						collapsingIndexes={[2]}
						data={order.files.map(file => [
							file.name,
							file.description || '—',
							<Button text='Ver propuesta' color='black' onClick={() => setViewingFile(file)} />
						])}
					/>
				)}
			</div>
			<Modal open={viewingFile !== null} basic onClose={bindClick(setViewingFile)}>
				{viewingFile !== null && (
					<Modal.Content>
						<div style={{ textAlign: 'center', marginBottom: 10, color: 'white', fontWeight: 'bold', fontSize: 16 }}>
							{viewingFile.name}
						</div>
						<img
							src={viewingFile.file_url}
							alt={viewingFile.name}
							style={{ width: '100%', maxWidth: 900, display: 'block', margin: 'auto', borderRadius: 12 }}
						/>
					</Modal.Content>
				)}
				<Modal.Actions>
					<Button onClick={bindClick(setViewingFile)} text='Cerrar' />
				</Modal.Actions>
			</Modal>
		</div>
	);
};

export default OrderView;